import { formatDistanceToNow, parseISO } from "date-fns";
import { el } from "date-fns/locale";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CircleAlert,
  Edit3,
  Mail,
  MapPin,
  MessageSquareOff,
  MoreVertical,
  Phone,
  Radar,
  Trash2,
} from "lucide-react";
import type { Partner } from "@/context/PartnerContext";
import ServiceBadge from "@/components/ui-custom/ServiceBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { PartnerMiniPageStatus } from "@/components/admin/PartnerMiniPageStatus";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const formatNumber = new Intl.NumberFormat("el-GR").format;

const relativeDate = (value?: string | null): string => {
  if (!value) return "Χωρίς lead";
  try {
    return formatDistanceToNow(parseISO(value), { addSuffix: true, locale: el });
  } catch {
    return value;
  }
};

const attentionLabel = (partner: Partner): string => {
  const reasons = partner.attentionReasons || [];
  if (reasons.includes("missing_services")) return "Χωρίς υπηρεσίες";
  if (reasons.includes("missing_coordinates")) return "Χωρίς συντεταγμένες";
  if (reasons.includes("missing_radius")) return "Χωρίς ακτίνα";
  if (reasons.includes("missing_notification_channel")) return "Χωρίς κανάλι ειδοποίησης";
  return "Χρειάζεται έλεγχο";
};

interface PartnerDirectoryCardProps {
  partner: Partner;
  onDelete: (partner: Partner) => void;
}

export function PartnerDirectoryCard({ partner, onDelete }: PartnerDirectoryCardProps) {
  const initials = partner.name.trim().slice(0, 2).toLocaleUpperCase("el-GR") || "Σ";
  const canDelete = (partner.leadCount || 0) === 0;
  const hasAttention = partner.active && (partner.attentionReasons?.length || 0) > 0;
  const coverageReady = partner.active && Boolean(partner.routingReady);

  return (
    <Card className="group min-w-0 rounded-2xl border-slate-200 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-md">
      <CardHeader className="space-y-4 p-4 pb-0 sm:p-5 sm:pb-0">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-bold ${coverageReady ? "bg-sky-950 text-white" : partner.active ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-600"}`}>
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <Link to={`/admin/partners/${partner.id}/dashboard`} className="block truncate text-base font-bold text-slate-950 hover:text-sky-700 hover:underline">
              {partner.name}
            </Link>
            <p className="mt-1 flex items-start gap-1.5 text-xs leading-5 text-slate-500">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="line-clamp-2">{partner.location || "Χωρίς καταχωρημένη περιοχή"}</span>
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0" aria-label={`Ενέργειες για ${partner.name}`}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>Διαχείριση συνεργάτη</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to={`/admin/partners/${partner.id}/dashboard`}><ArrowRight className="mr-2 h-4 w-4" /> Dashboard</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/admin/partners/edit/${partner.id}`}><Edit3 className="mr-2 h-4 w-4" /> Επεξεργασία</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!canDelete}
                onClick={() => canDelete && onDelete(partner)}
                className="text-rose-700 focus:bg-rose-50 focus:text-rose-800"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {canDelete ? "Οριστική διαγραφή" : "Δεν διαγράφεται — έχει leads"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className={partner.active ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-100 text-slate-600"}>
            {partner.active ? "Ενεργός" : "Ανενεργός"}
          </Badge>
          {partner.active && (
            <Badge variant="outline" className={coverageReady ? "border-sky-200 bg-sky-50 text-sky-800" : "border-amber-200 bg-amber-50 text-amber-800"}>
              {coverageReady ? <Radar className="mr-1 h-3 w-3" /> : <CircleAlert className="mr-1 h-3 w-3" />}
              {coverageReady ? "Έτοιμος για routing" : attentionLabel(partner)}
            </Badge>
          )}
          {partner.sms_opt_out && (
            <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600"><MessageSquareOff className="mr-1 h-3 w-3" /> Χωρίς SMS</Badge>
          )}
          <PartnerMiniPageStatus partner={partner} />
        </div>
      </CardHeader>

      <CardContent className="space-y-4 p-4 sm:p-5">
        <div className="grid gap-2 sm:grid-cols-2">
          {partner.phone ? (
            <a href={`tel:${partner.phone.replace(/\s+/g, "")}`} className="flex min-h-11 min-w-0 items-center gap-2 rounded-xl bg-slate-50 px-3 text-sm text-slate-700 transition hover:bg-sky-50 hover:text-sky-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600">
              <Phone className="h-4 w-4 shrink-0 text-slate-400" /><span className="truncate">{partner.phone}</span>
            </a>
          ) : (
            <div className="flex min-h-11 items-center gap-2 rounded-xl bg-amber-50 px-3 text-sm text-amber-800"><Phone className="h-4 w-4" /> Χωρίς τηλέφωνο</div>
          )}
          {partner.email ? (
            <a href={`mailto:${partner.email}`} className="flex min-h-11 min-w-0 items-center gap-2 rounded-xl bg-slate-50 px-3 text-sm text-slate-700 transition hover:bg-sky-50 hover:text-sky-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600">
              <Mail className="h-4 w-4 shrink-0 text-slate-400" /><span className="truncate">{partner.email}</span>
            </a>
          ) : (
            <div className="flex min-h-11 items-center gap-2 rounded-xl bg-amber-50 px-3 text-sm text-amber-800"><Mail className="h-4 w-4" /> Χωρίς email</div>
          )}
        </div>

        <div className="grid grid-cols-3 divide-x divide-slate-100 rounded-xl border border-slate-100 bg-white py-3 text-center">
          <div className="px-2"><p className="text-lg font-bold tabular-nums text-slate-950">{formatNumber(partner.leadCount || 0)}</p><p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">Σύνολο</p></div>
          <div className="px-2"><p className="text-lg font-bold tabular-nums text-sky-700">{formatNumber(partner.leads30 || 0)}</p><p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">30 ημέρες</p></div>
          <div className="min-w-0 px-2"><p className="truncate text-sm font-bold text-slate-800">{relativeDate(partner.lastLead)}</p><p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">Τελευταίο</p></div>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-xs">
          <span className="text-slate-500">Κάλυψη δικτύου</span>
          <span className={`font-semibold ${partner.routingReady ? "text-emerald-700" : "text-amber-700"}`}>
            {partner.routingReady ? `${partner.radius || 0} km ακτίνα` : "Χρειάζεται ρύθμιση"}
          </span>
        </div>

        <div className="flex min-h-7 flex-wrap gap-1.5">
          {partner.services.length > 0 ? partner.services.map((service) => <ServiceBadge key={service} service={service} />) : <span className="text-xs text-slate-400">Δεν έχουν δηλωθεί υπηρεσίες</span>}
        </div>
      </CardContent>

      <CardFooter className="grid grid-cols-[1fr_auto] gap-2 border-t border-slate-100 p-4 sm:px-5">
        <Button asChild className="h-11 justify-between bg-slate-950 text-white hover:bg-slate-800">
          <Link to={`/admin/partners/${partner.id}/dashboard`}>Dashboard <ArrowRight className="h-4 w-4" /></Link>
        </Button>
        <Button asChild variant="outline" size="icon" className="h-11 w-11" aria-label={`Επεξεργασία ${partner.name}`}>
          <Link to={`/admin/partners/edit/${partner.id}`}><Edit3 className="h-4 w-4" /></Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
